import type { Transition, Variants } from "motion/react";

/**
 * The platform's motion vocabulary.
 *
 * Three rules, and they are restrictions rather than a palette:
 *
 *   1. **Nothing animates on mount.** Internal screens load into a task; a Lead
 *      opening the register forty times a day does not want to watch it arrive.
 *      Content is visible by default. The exception is the client portal, which
 *      a client sees once a fortnight and where the first view is the firm's
 *      face — one restrained settle, not a sequence.
 *
 *   2. **Motion reports a change.** A chip morphs so you see it change, a new
 *      register entry rules itself in, a count rolls. If it does not say
 *      something changed, it does not move.
 *
 *   3. **Damped, single axis, no overshoot.** The brand does not bounce. 150–220ms,
 *      exponential ease-out from an already-visible default.
 *
 * `prefers-reduced-motion` is handled in CSS for transitions and here for
 * anything JS-driven, so a reduced-motion user gets instant, legible swaps
 * rather than a slower version of the same choreography.
 */

export const prefersReducedMotion = (): boolean =>
  typeof window !== "undefined" &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

/** The default: fast, damped, no overshoot. */
export const damped: Transition = {
  duration: 0.18,
  ease: [0.2, 0, 0, 1],
};

/** Slightly longer, for something the eye must follow across a distance. */
export const carried: Transition = {
  duration: 0.3,
  ease: [0.2, 0, 0, 1],
};

/**
 * The one authored moment: publication.
 *
 * Releasing something to a client is deliberate, attributed and consequential —
 * the only irreversible-in-effect act in the product. It gets the only
 * orchestrated sequence, and it reads as a crossing: the entry leaves the
 * internal side, the detent fills, the register gains a line.
 */
export const release: Transition = {
  duration: 0.46,
  ease: [0.16, 1, 0.3, 1],
};

/** A row arriving in the register, because a fact was just recorded. */
export const entryArrives: Variants = {
  initial: { opacity: 0, y: -6 },
  settled: { opacity: 1, y: 0, transition: damped },
};

/**
 * The client portal's first view. One settle for the whole composition rather
 * than a stagger down the page — a sequence would make the firm look slow.
 */
export const portalSettles: Variants = {
  initial: { opacity: 0, y: 8 },
  settled: { opacity: 1, y: 0, transition: { duration: 0.42, ease: [0.16, 1, 0.3, 1] } },
};

/** Expand and collapse that keeps the surrounding rules from jumping. */
export const disclose: Variants = {
  closed: { height: 0, opacity: 0, transition: damped },
  open: { height: "auto", opacity: 1, transition: damped },
};
